import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useParams, Link, useNavigate } from "react-router-dom";
import { api, canonicalBase, formatPriceLocal } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { ChevronRight, ChevronDown, SlidersHorizontal, X } from "lucide-react";
import SEO from "../components/SEO";

const SORT_OPTIONS = [
  { id: "newest", label: "Nejnovější" },
  { id: "popular", label: "Nejoblíbenější" },
  { id: "price_asc", label: "Cena: od nejlevnějšího" },
  { id: "price_desc", label: "Cena: od nejdražšího" },
  { id: "name_asc", label: "Název: A–Z" },
];

export default function Products() {
  const { slug: catSlug } = useParams();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const label = search.get("label") || "";
  const q = search.get("q") || "";
  const sort = search.get("sort") || "newest";
  const attrsStr = search.get("attrs") || "";
  const brandsStr = search.get("brands") || "";
  const priceMinParam = search.get("price_min");
  const priceMaxParam = search.get("price_max");

  const [tree, setTree] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [products, setProducts] = useState([]);
  const [facets, setFacets] = useState({ attributes: {}, brands: [], price: { min: 0, max: 0 } });
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false); // mobile drawer

  // Lock body scroll & ensure mobile drawer stays above header while open
  useEffect(() => {
    if (!filtersOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [filtersOpen]);
  const [priceSlider, setPriceSlider] = useState({ min: null, max: null }); // local before commit

  // Load tree once
  useEffect(() => { api.get("/categories").then((r) => setTree(r.data)); }, []);

  // Load active category meta
  useEffect(() => {
    if (catSlug) {
      api.get(`/categories/${catSlug}`).then((r) => setActiveCat(r.data)).catch(() => setActiveCat(null));
    } else {
      setActiveCat(null);
    }
  }, [catSlug]);

  // Selected attributes parsed
  const selectedAttrs = useMemo(() => {
    const map = {};
    attrsStr.split(",").filter(Boolean).forEach((p) => {
      const [k, v] = p.split(":");
      if (k && v) (map[k] = map[k] || []).push(v);
    });
    return map;
  }, [attrsStr]);

  const toggleAttr = (k, v) => {
    const current = { ...selectedAttrs };
    const arr = current[k] || [];
    current[k] = arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
    const newStr = Object.entries(current)
      .filter(([, vals]) => vals.length)
      .flatMap(([key, vals]) => vals.map((val) => `${key}:${val}`))
      .join(",");
    const sp = new URLSearchParams(search);
    if (newStr) sp.set("attrs", newStr); else sp.delete("attrs");
    setSearch(sp, { replace: true });
  };

  const setSort = (s) => {
    const sp = new URLSearchParams(search);
    sp.set("sort", s);
    setSearch(sp, { replace: true });
  };

  const selectedBrands = useMemo(() => brandsStr ? brandsStr.split(",").filter(Boolean) : [], [brandsStr]);
  const toggleBrand = (b) => {
    const cur = selectedBrands.includes(b) ? selectedBrands.filter((x) => x !== b) : [...selectedBrands, b];
    const sp = new URLSearchParams(search);
    if (cur.length) sp.set("brands", cur.join(",")); else sp.delete("brands");
    setSearch(sp, { replace: true });
  };

  // Sync slider position when facets/url change
  useEffect(() => {
    const fmin = facets.price?.min ?? 0;
    const fmax = facets.price?.max ?? 0;
    setPriceSlider({
      min: priceMinParam !== null ? Number(priceMinParam) : fmin,
      max: priceMaxParam !== null ? Number(priceMaxParam) : fmax,
    });
  }, [facets.price?.min, facets.price?.max, priceMinParam, priceMaxParam]);

  const commitPrice = (next) => {
    const sp = new URLSearchParams(search);
    const fmin = facets.price?.min ?? 0;
    const fmax = facets.price?.max ?? 0;
    if (next.min > fmin) sp.set("price_min", String(next.min)); else sp.delete("price_min");
    if (next.max < fmax) sp.set("price_max", String(next.max)); else sp.delete("price_max");
    setSearch(sp, { replace: true });
  };

  // Fetch products
  useEffect(() => {
    setLoading(true);
    const params = { sort };
    if (catSlug) params.category_slug = catSlug;
    if (label) params.label = label;
    if (q) params.q = q;
    if (attrsStr) params.attrs = attrsStr;
    if (brandsStr) params.brands = brandsStr;
    if (priceMinParam !== null) params.price_min = priceMinParam;
    if (priceMaxParam !== null) params.price_max = priceMaxParam;
    api.get("/products", { params }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [catSlug, label, q, sort, attrsStr, brandsStr, priceMinParam, priceMaxParam]);

  // Fetch facets per category
  useEffect(() => {
    const params = {};
    if (catSlug) params.category_slug = catSlug;
    api.get("/products/attributes/available", { params }).then((r) => setFacets(r.data || { attributes: {}, brands: [], price: { min: 0, max: 0 } }));
  }, [catSlug]);

  const hasActiveFilters = !!(attrsStr || brandsStr || priceMinParam || priceMaxParam || label);

  const clearAll = () => {
    const sp = new URLSearchParams();
    if (sort !== "newest") sp.set("sort", sort);
    setSearch(sp, { replace: true });
    if (catSlug) navigate("/produkty");
  };
  const breadcrumbs = activeCat?.breadcrumbs || [];

  return (
    <div className="pt-8 pb-20" data-testid="products-page">
      <SEO
        title={activeCat?.name ? `${activeCat.name} – katalog licencí` : "Katalog všech produktů"}
        description={activeCat?.description || `Originální digitální licence ${activeCat?.name || "Windows, Office, Adobe a antivirů"} s okamžitým doručením a fakturou.`}
        keywords={`${activeCat?.name || "software"}, licence, digitální klíč, okamžité doručení`}
        jsonLd={breadcrumbs.length > 0 ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Domů", item: canonicalBase() + "/" },
            { "@type": "ListItem", position: 2, name: "Produkty", item: canonicalBase() + "/produkty" },
            ...breadcrumbs.map((b, i) => ({
              "@type": "ListItem",
              position: 3 + i,
              name: b.name,
              item: canonicalBase() + `/produkty/kategorie/${b.slug}`,
            })),
          ],
        } : null}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold mb-2">Katalog</div>
          {breadcrumbs.length > 0 ? (
            <div className="flex items-center gap-1.5 text-sm text-zinc-500 mb-2 flex-wrap" data-testid="breadcrumbs">
              <Link to="/produkty" className="hover:text-zinc-900">Všechny produkty</Link>
              {breadcrumbs.map((b, i) => (
                <span key={b.id} className="inline-flex items-center gap-1.5">
                  <ChevronRight className="w-3 h-3"/>
                  {i === breadcrumbs.length - 1 ? (
                    <span className="text-zinc-900 font-medium">{b.name}</span>
                  ) : (
                    <Link to={`/produkty/kategorie/${b.slug}`} className="hover:text-zinc-900">{b.name}</Link>
                  )}
                </span>
              ))}
            </div>
          ) : null}
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">{activeCat?.name || "Všechny produkty"}</h1>
          <p className="text-zinc-600 mt-2">Originální digitální licence s okamžitým doručením.</p>
        </div>

        <div className="flex gap-8">
          {/* Sidebar */}
          <aside
            className={`${filtersOpen ? "fixed inset-0 bg-black/50 z-[60] lg:static lg:bg-transparent lg:z-auto" : "hidden"} lg:block lg:w-72 lg:flex-shrink-0`}
            data-testid="products-sidebar"
            onClick={(e) => { if (filtersOpen && e.target === e.currentTarget) setFiltersOpen(false); }}
          >
            <div
              className={`${filtersOpen ? "absolute right-0 top-0 bottom-0 w-80 max-w-[85vw] overflow-y-auto pt-[env(safe-area-inset-top,0)]" : ""} bg-white lg:bg-transparent p-5 lg:p-0 space-y-6`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="lg:hidden sticky top-0 -mx-5 -mt-5 px-5 py-3 bg-white border-b border-zinc-200 flex justify-between items-center z-10">
                <div className="font-display font-bold text-lg">Filtry</div>
                <button onClick={() => setFiltersOpen(false)} className="p-2 -mr-2" data-testid="close-filters" aria-label="Zavřít filtry"><X className="w-5 h-5"/></button>
              </div>

              <div>
                <div className="font-display font-bold text-sm uppercase tracking-wider text-zinc-500 mb-3">Kategorie</div>
                <div className="space-y-0.5" data-testid="cat-tree">
                  <Link to="/produkty" className={`block px-3 py-1.5 rounded-lg text-sm transition-colors ${!catSlug ? "bg-zinc-900 text-white font-medium" : "hover:bg-zinc-100"}`}>Vše</Link>
                  {tree.length > 0 && <CategoryTree tree={tree} activeSlug={catSlug}/>}
                </div>
              </div>

              {facets.brands && facets.brands.length > 0 && (
                <div data-testid="brand-filter">
                  <div className="font-display font-bold text-sm uppercase tracking-wider text-zinc-500 mb-3">Značka</div>
                  <div className="space-y-1">
                    {facets.brands.map((b) => (
                      <label key={b} className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer hover:text-zinc-900">
                        <input type="checkbox" checked={selectedBrands.includes(b)} onChange={() => toggleBrand(b)} data-testid={`brand-${b}`}/>
                        <span>{b}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {facets.price && facets.price.max > facets.price.min && (
                <div data-testid="price-filter">
                  <div className="font-display font-bold text-sm uppercase tracking-wider text-zinc-500 mb-3">Rozsah ceny</div>
                  <PriceRange
                    bounds={facets.price}
                    value={priceSlider}
                    onChange={setPriceSlider}
                    onCommit={commitPrice}
                  />
                </div>
              )}

              {facets.attributes && Object.keys(facets.attributes).length > 0 && (
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <div className="font-display font-bold text-sm uppercase tracking-wider text-zinc-500">Vlastnosti</div>
                    {hasActiveFilters && (
                      <button onClick={clearAll} className="text-xs text-blue-600 hover:underline" data-testid="clear-filters">Vyčistit</button>
                    )}
                  </div>
                  {Object.entries(facets.attributes).map(([k, values]) => (
                    <div key={k} className="mb-4">
                      <div className="text-xs font-semibold text-zinc-700 mb-1.5">{k}</div>
                      <div className="space-y-1">
                        {values.map((v) => (
                          <label key={v} className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer hover:text-zinc-900">
                            <input type="checkbox" checked={(selectedAttrs[k] || []).includes(v)} onChange={() => toggleAttr(k, v)} data-testid={`attr-${k}-${v}`}/>
                            <span>{v}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>

          {/* Main */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
              <button onClick={() => setFiltersOpen(true)} className="lg:hidden inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border border-zinc-200 bg-white" data-testid="open-filters"><SlidersHorizontal className="w-4 h-4"/>Filtry</button>
              <div className="text-sm text-zinc-500" data-testid="product-count">{loading ? "Načítání…" : `${products.length} produktů`}</div>
              <div className="ml-auto flex items-center gap-2">
                <label className="text-xs text-zinc-500">Seřadit:</label>
                <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 rounded-full text-sm font-medium border border-zinc-200 bg-white focus:outline-none focus:border-zinc-400" data-testid="sort-select">
                  {SORT_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {[...Array(6)].map((_, i) => <div key={i} className="aspect-[3/4] bg-zinc-100 rounded-2xl animate-pulse"/>)}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20 text-zinc-500">
                <div className="mb-4">Žádné produkty neodpovídají filtru.</div>
                <button onClick={clearAll} className="text-sm font-semibold text-blue-600 hover:underline" data-testid="empty-clear">Vyčistit filtry</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {products.map((p) => <ProductCard key={p.id} product={p}/>)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PriceRange({ bounds, value, onChange, onCommit }) {
  const min = bounds.min || 0;
  const max = bounds.max || 0;
  const curMin = value.min ?? min;
  const curMax = value.max ?? max;
  const step = max - min > 5000 ? 100 : max - min > 500 ? 50 : 10;
  const fmt = (v) => new Intl.NumberFormat("cs-CZ").format(Math.round(v || 0));

  const onMin = (e) => {
    const v = Math.min(Number(e.target.value), curMax - step);
    onChange({ min: v, max: curMax });
  };
  const onMax = (e) => {
    const v = Math.max(Number(e.target.value), curMin + step);
    onChange({ min: curMin, max: v });
  };

  const pctMin = max > min ? ((curMin - min) / (max - min)) * 100 : 0;
  const pctMax = max > min ? ((curMax - min) / (max - min)) * 100 : 100;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm tabular-nums">
        <span className="font-semibold text-zinc-900" data-testid="price-cur-min">{formatPriceLocal(curMin)}</span>
        <span className="text-zinc-400">—</span>
        <span className="font-semibold text-zinc-900" data-testid="price-cur-max">{formatPriceLocal(curMax)}</span>
      </div>
      <div className="relative h-8">
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1 bg-zinc-200 rounded-full"/>
        <div
          className="absolute top-1/2 -translate-y-1/2 h-1 bg-blue-600 rounded-full"
          style={{ left: `${pctMin}%`, right: `${100 - pctMax}%` }}
        />
        <input
          type="range" min={min} max={max} step={step} value={curMin}
          onChange={onMin}
          onMouseUp={() => onCommit({ min: curMin, max: curMax })}
          onTouchEnd={() => onCommit({ min: curMin, max: curMax })}
          className="absolute inset-0 w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-blue-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow"
          data-testid="price-min-slider"
        />
        <input
          type="range" min={min} max={max} step={step} value={curMax}
          onChange={onMax}
          onMouseUp={() => onCommit({ min: curMin, max: curMax })}
          onTouchEnd={() => onCommit({ min: curMin, max: curMax })}
          className="absolute inset-0 w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-blue-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow"
          data-testid="price-max-slider"
        />
      </div>
      <div className="flex gap-2">
        <div className="flex-1">
          <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Od</label>
          <input
            type="number" min={min} max={curMax - step} step={step} value={curMin}
            onChange={onMin}
            onBlur={() => onCommit({ min: curMin, max: curMax })}
            className="w-full px-2 py-1.5 rounded-lg border border-zinc-200 text-sm tabular-nums focus:outline-none focus:border-zinc-400"
            data-testid="price-min-input"
          />
        </div>
        <div className="flex-1">
          <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Do</label>
          <input
            type="number" min={curMin + step} max={max} step={step} value={curMax}
            onChange={onMax}
            onBlur={() => onCommit({ min: curMin, max: curMax })}
            className="w-full px-2 py-1.5 rounded-lg border border-zinc-200 text-sm tabular-nums focus:outline-none focus:border-zinc-400"
            data-testid="price-max-input"
          />
        </div>
      </div>
    </div>
  );
}

function CategoryTree({ tree, activeSlug }) {
  const [openIds, setOpenIds] = useState(() => new Set());
  // Auto-open path to active slug
  useEffect(() => {
    if (!activeSlug) return;
    const path = new Set();
    function walk(nodes, parents) {
      for (const n of nodes) {
        if (n.slug === activeSlug) {
          parents.forEach((p) => path.add(p));
          return true;
        }
        if (walk(n.children || [], [...parents, n.id])) {
          parents.forEach((p) => path.add(p));
          return true;
        }
      }
      return false;
    }
    walk(tree, []);
    setOpenIds((cur) => { const n = new Set(cur); path.forEach((id) => n.add(id)); return n; });
  }, [activeSlug, tree]);

  const toggle = (id) => setOpenIds((cur) => {
    const n = new Set(cur);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  function renderItems(nodes, depth) {
    return nodes.map((cat) => {
      const hasKids = (cat.children || []).length > 0;
      const isActive = activeSlug === cat.slug;
      const open = openIds.has(cat.id);
      return (
        <div key={cat.id}>
          <div className="flex items-center">
            <Link
              to={`/produkty/kategorie/${cat.slug}`}
              className={`flex-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${isActive ? "bg-zinc-900 text-white font-medium" : "hover:bg-zinc-100 text-zinc-700"}`}
              style={{ paddingLeft: `${depth * 12 + 12}px` }}
              data-testid={`sidebar-cat-${cat.slug}`}
            >
              {cat.icon ? `${cat.icon} ` : ""}{cat.name}
              <span className="ml-1.5 text-xs text-zinc-400">({cat.product_count})</span>
            </Link>
            {hasKids ? (
              <button onClick={() => toggle(cat.id)} className="p-1 text-zinc-400 hover:text-zinc-700" data-testid={`sidebar-cat-toggle-${cat.slug}`}>
                {open ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
              </button>
            ) : null}
          </div>
          {open && hasKids ? <div>{renderItems(cat.children, depth + 1)}</div> : null}
        </div>
      );
    });
  }

  return <>{renderItems(tree, 0)}</>;
}
